import {loggerFactory} from "@cm/browser-log"
import {getIOSVersion, getSafariVersion, isIoS, isIpadOS} from "@cm/browser-utils"
import {Matrix4, Vector3} from "@cm/math"
import {SceneNodes, ObjectPath} from "@cm/template-nodes"
import {Three as THREE, ThreeMeshBvh} from "@cm/material-nodes/three"

export type ThreeFloatTextureType = typeof THREE.HalfFloatType | typeof THREE.FloatType
export let DEFAULT_FLOAT_TEXTURE_TYPE: ThreeFloatTextureType = THREE.HalfFloatType
const threeUtilsLogger = loggerFactory.getLogger("scene")

if (isIpadOS) {
    const versionFields = getSafariVersion()
    if (versionFields) {
        const [major, minor, rev] = versionFields
        if (major == 14) {
            // THREE.FloatType textures render all black on iPadOS Safari 14...
            DEFAULT_FLOAT_TEXTURE_TYPE = THREE.HalfFloatType
        } else if (major <= 13) {
            threeUtilsLogger.warn(`Enabling workaround for half-float textures on iPadOS Safari ${major}.${minor}.${rev}`)
            DEFAULT_FLOAT_TEXTURE_TYPE = THREE.FloatType
        }
    } else {
        threeUtilsLogger.warn("Can't detect iPadOS Safari version! No WebGL workarounds applied.")
    }
} else if (isIoS) {
    const versionFields = getIOSVersion()
    if (versionFields) {
        const [major, minor, rev] = versionFields
        if (major == 14) {
            // THREE.FloatType textures render all black on iOS 14...
            DEFAULT_FLOAT_TEXTURE_TYPE = THREE.HalfFloatType
        } else if (major <= 13) {
            threeUtilsLogger.warn(`Enabling workaround for half-float textures on iOS ${major}.${minor}.${rev}`)
            DEFAULT_FLOAT_TEXTURE_TYPE = THREE.FloatType
        }
    } else {
        threeUtilsLogger.warn("Can't detect iOS version! No WebGL workarounds applied.")
    }
}

export function toThreeMatrix(matrix: Matrix4): THREE.Matrix4 {
    return new THREE.Matrix4().fromArray(matrix.toArray())
}

export function fromThreeMatrix(matrix: THREE.Matrix4): Matrix4 {
    return Matrix4.fromArray(matrix.elements)
}

export function toThreeVector(vector: Vector3): THREE.Vector3 {
    return new THREE.Vector3(vector.x, vector.y, vector.z)
}

export function clampColor01(color: THREE.Color): THREE.Color {
    color.r = THREE.MathUtils.clamp(color.r, 0, 1)
    color.g = THREE.MathUtils.clamp(color.g, 0, 1)
    color.b = THREE.MathUtils.clamp(color.b, 0, 1)
    return color
}

export function fromThreeVector(vector: THREE.Vector3): Vector3 {
    return new Vector3(vector.x, vector.y, vector.z)
}

export function flipThreeSide(side: THREE.Side): THREE.Side {
    switch (side) {
        case THREE.FrontSide:
            return THREE.BackSide
        case THREE.BackSide:
            return THREE.FrontSide
        default:
            return THREE.DoubleSide
    }
}

export function getAuthoredMaterialSide(material: THREE.Material): THREE.Side {
    return material.shadowSide !== null ? flipThreeSide(material.shadowSide) : material.side
}

export function getRenderingContext(renderer: THREE.WebGPURenderer | undefined): {gl: WebGL2RenderingContext} | {device: GPUDevice} | undefined {
    const context = renderer?.getContext()
    if (context instanceof WebGL2RenderingContext) return {gl: context}
    if (renderer?.backend instanceof THREE.WebGPUBackend && context instanceof GPUCanvasContext) {
        const device = context.getConfiguration?.()?.device
        if (device) return {device}
    }
    return undefined
}

export const MIN_NEAR_CLIP = 1
export const MAX_FAR_CLIP = 20000
export type CameraParameters = Pick<
    SceneNodes.Camera,
    "nearClip" | "farClip" | "filmGauge" | "focalLength" | "shiftX" | "shiftY" | "resolutionX" | "resolutionY"
>

export const setThreeCameraTransform = (threeCamera: THREE.PerspectiveCamera, transform: Matrix4 | THREE.Matrix4) => {
    const threeMatrix = transform instanceof THREE.Matrix4 ? transform : toThreeMatrix(transform)

    threeCamera.matrix = threeMatrix.clone()
    threeCamera.matrix.decompose(threeCamera.position, threeCamera.quaternion, threeCamera.scale)

    threeCamera.updateMatrixWorld() // needed if camera has no parent
}

export const updateThreeCamera = (threeCamera: THREE.PerspectiveCamera, parameters: CameraParameters, transform?: Matrix4 | THREE.Matrix4) => {
    const {nearClip, farClip, filmGauge, focalLength, shiftX, shiftY, resolutionX, resolutionY} = parameters
    const aspectRatio = resolutionX / resolutionY

    threeCamera.near = Math.max(MIN_NEAR_CLIP, nearClip ?? MIN_NEAR_CLIP)
    threeCamera.far = Math.min(MAX_FAR_CLIP, farClip ?? MAX_FAR_CLIP)
    threeCamera.clearViewOffset()
    threeCamera.aspect = aspectRatio
    threeCamera.filmGauge = filmGauge
    threeCamera.setFocalLength(focalLength)
    if (aspectRatio > 1.0) threeCamera.setViewOffset(1, 1 / aspectRatio, shiftX, -shiftY / aspectRatio, 1, 1 / aspectRatio)
    else threeCamera.setViewOffset(aspectRatio, 1, shiftX * aspectRatio, -shiftY, aspectRatio, 1)
    if (transform) setThreeCameraTransform(threeCamera, transform)

    threeCamera.updateProjectionMatrix()

    return threeCamera
}

export type SceneCameraParameters = CameraParameters &
    Pick<SceneNodes.Camera, "focalDistance" | "fStop" | "exposure" | "realtimeSettings" | "target" | "targeted" | "autoFocus" | "zoomFactor">

export function threeCameraToSceneNode(
    threeCamera: THREE.PerspectiveCamera,
    id: SceneNodes.Camera["id"],
    path: ObjectPath,
    renderDimensions: {
        width: number
        height: number
    },
    parameters?: Partial<SceneCameraParameters>,
): SceneNodes.Camera {
    let shiftX = 0,
        shiftY = 0
    if (threeCamera.view?.enabled) {
        if (threeCamera.aspect > 1.0) {
            shiftX = threeCamera.view.offsetX
            shiftY = -threeCamera.view.offsetY * threeCamera.aspect
        } else {
            shiftX = threeCamera.view.offsetX / threeCamera.aspect
            shiftY = -threeCamera.view.offsetY
        }
    }
    return {
        type: "Camera",
        templateDepth: 0,
        id,
        path,
        nearClip: parameters?.nearClip ?? threeCamera.near,
        farClip: parameters?.farClip ?? threeCamera.far,
        filmGauge: parameters?.filmGauge ?? threeCamera.filmGauge,
        focalLength: parameters?.focalLength ?? threeCamera.getFocalLength(),
        shiftX: parameters?.shiftX ?? shiftX,
        shiftY: parameters?.shiftY ?? shiftY,
        zoomFactor: parameters?.zoomFactor ?? 1,
        resolutionX: renderDimensions.width,
        resolutionY: renderDimensions.height,
        focalDistance: parameters?.focalDistance ?? 100000,
        fStop: parameters?.fStop ?? 10000,
        autoFocus: parameters?.autoFocus ?? false,
        target: parameters?.target ?? new Vector3(0, 0, 0),
        targeted: parameters?.targeted ?? false,
        exposure: parameters?.exposure ?? 1,
        transform: fromThreeMatrix(new THREE.Matrix4().compose(threeCamera.position, threeCamera.quaternion, threeCamera.scale)),
        realtimeSettings: parameters?.realtimeSettings ?? {
            enableDamping: false,
            dampingFactor: 0.05,
            toneMapping: {
                mode: "linear",
            },
            enableOrbiting: true,
            enablePanning: true,
            screenSpacePanning: true,
            adaptAspectRatio: true,
        },
    }
}

export class Float16ArrayBuilder {
    private tmpFloat32 = new Float32Array(1)
    private tmpUint32View = new Uint32Array(this.tmpFloat32.buffer)
    readonly array: Uint16Array
    constructor(length: number) {
        this.array = new Uint16Array(length)
    }

    set(idx: number, value: number): void {
        this.array[idx] = THREE.DataUtils.toHalfFloat(value)
    }
}

export class Float32ArrayBuilder {
    readonly array: Float32Array
    constructor(length: number) {
        this.array = new Float32Array(length)
    }
    set(idx: number, value: number): void {
        this.array[idx] = value
    }
}

const raycaster = new THREE.Raycaster()
raycaster.firstHitOnly = true

export function projectCurvePointsToObject(
    curvePoints: {
        points: Float32Array
        normals: Float32Array
    },
    transform: THREE.Matrix4,
    queryRange: number,
    scene: THREE.Object3D,
) {
    const normalMatrix = new THREE.Matrix3().setFromMatrix4(transform)
    const inverseTransform = transform.clone().invert()

    scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
            const mesh = object as THREE.Mesh<THREE.BufferGeometry>
            if (!mesh.geometry.boundsTree) mesh.geometry.computeBoundsTree()
        }
    })

    raycaster.near = 0
    raycaster.far = 2 * queryRange

    const {points, normals} = curvePoints

    if (points.length !== normals.length || points.length % 3 !== 0) throw new Error("Invalid curve points")

    const numPoints = points.length / 3
    const hitPoints = Array.from({length: numPoints}, () => new THREE.Vector3())
    const hitNormals = Array.from({length: numPoints}, () => new THREE.Vector3())
    const valid = Array.from({length: numPoints}, () => false)
    for (let i = 0; i < numPoints; i++) {
        const point = new THREE.Vector3(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]).applyMatrix4(transform)
        const normal = new THREE.Vector3(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]).applyMatrix3(normalMatrix)

        const origin = point.clone().add(normal.clone().multiplyScalar(queryRange))
        raycaster.set(origin, normal.clone().negate())
        const intersections = raycaster.intersectObject(scene, true)
        if (intersections.length > 0) {
            const intersection = intersections[0]
            const {point, normal} = intersection
            if (normal) {
                hitPoints[i] = point.clone().applyMatrix4(inverseTransform)
                hitNormals[i] = normal.normalize()

                valid[i] = true
            }
        }
    }

    return {hitPoints, hitNormals, valid}
}

export function closestCurvePointsToObject(queryPoints: Float32Array, transform: THREE.Matrix4, queryRange: number, scene: THREE.Object3D) {
    type QueryMesh = {
        bvh: ThreeMeshBvh.MeshBVH
        bvhToWorld: THREE.Matrix4
        worldToBvh: THREE.Matrix4
    }

    const meshes: QueryMesh[] = []
    scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
            const mesh = object as THREE.Mesh<THREE.BufferGeometry>
            if (!mesh.geometry.boundsTree) mesh.geometry.computeBoundsTree()
            const bvh = mesh.geometry.boundsTree
            if (!(bvh instanceof ThreeMeshBvh.MeshBVH)) throw new Error("Mesh does not have a MeshBVH bounds tree")

            mesh.updateMatrixWorld()

            meshes.push({
                bvh,
                bvhToWorld: mesh.matrixWorld,
                worldToBvh: mesh.matrixWorld.clone().invert(),
            })
        }
    })

    const numPoints = queryPoints.length / 3
    const inverseTransform = transform.clone().invert()

    const hitPoints = Array.from({length: numPoints}, () => new THREE.Vector3())
    const hitNormals = Array.from({length: numPoints}, () => new THREE.Vector3())
    const valid = Array.from({length: numPoints}, () => false)

    for (let i = 0; i < numPoints; i++) {
        const worldPoint = new THREE.Vector3(queryPoints[i * 3], queryPoints[i * 3 + 1], queryPoints[i * 3 + 2]).applyMatrix4(transform)

        let closestHit: (ThreeMeshBvh.HitPointInfo & {queryMesh: QueryMesh}) | undefined = undefined

        for (const queryMesh of meshes) {
            const {bvh, worldToBvh} = queryMesh
            const bvhPoint = worldPoint.clone().applyMatrix4(worldToBvh)
            const result = bvh.closestPointToPoint(bvhPoint, undefined, undefined, queryRange)
            if (result) {
                const {distance} = result
                if (!closestHit || closestHit.distance > distance) {
                    closestHit = {
                        ...result,
                        queryMesh,
                    }
                }
            }
        }

        if (closestHit) {
            const {point, faceIndex, queryMesh} = closestHit
            const result = ThreeMeshBvh.getTriangleHitPointInfo(point, queryMesh.bvh.geometry, faceIndex)

            const worldPoint = point.clone().applyMatrix4(queryMesh.bvhToWorld)
            const worldNormal = result.face.normal.clone().applyMatrix3(new THREE.Matrix3().setFromMatrix4(queryMesh.bvhToWorld))

            const localPoint = worldPoint.clone().applyMatrix4(inverseTransform)
            const localNormal = worldNormal.clone().applyMatrix3(new THREE.Matrix3().setFromMatrix4(inverseTransform)).normalize()

            hitPoints[i] = localPoint
            hitNormals[i] = localNormal
            valid[i] = true
        } else valid[i] = false
    }

    return {hitPoints, hitNormals, valid}
}
